import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { TeamLogoUpload } from './TeamLogoUpload';
import { useTeams } from '@/hooks/useTeams';
import { Team } from '@/types/database';
import { toast } from 'sonner';

interface EditTeamModalProps {
  isOpen: boolean;
  onClose: () => void;
  team: Team;
  onTeamUpdate?: (updatedTeam: Team) => void;
}

const EditTeamModal: React.FC<EditTeamModalProps> = ({ 
  isOpen, 
  onClose, 
  team,
  onTeamUpdate 
}) => {
  const { updateTeam } = useTeams();
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    team_name: team.team_name || '',
    team_description: team.team_description || '',
    sport_type: team.sport_type || '',
    age_group: team.age_group || '',
    division: team.division || '',
    continent: team.continent || '',
    state_province: team.state_province || '',
    city: team.city || '',
    team_logo: team.team_logo || ''
  });

  const sportsOptions = [
    'Football', 'Basketball', 'Baseball', 'Soccer', 'Tennis', 'Volleyball', 
    'Swimming', 'Track & Field', 'Wrestling', 'Golf', 'Hockey', 'Softball'
  ];

  const ageGroupOptions = [
    'Age 1', 'Age 2', 'Age 3', 'Age 4', 'Age 5', 'Age 6', 'Age 7', 'Age 8', 'Age 9', 'Age 10', 'Age 11', 'Age 12', 'Age 13',
    '13 and under', '14-17', '18-plus'
  ];

  const divisionOptions = [
    'Recreational', 'Competitive', 'Elite', 'Professional', 'Beginner', 
    'Intermediate', 'Advanced', 'Division 1', 'Division 2', 'Division 3'
  ];

  const continentOptions = [
    'North America', 'South America', 'Europe', 'Africa', 'Asia', 'Australia', 'Antarctica'
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.team_name.trim()) {
      toast.error('Team name is required');
      return;
    }

    try {
      setLoading(true);
      
      // Construct location from city, state, continent
      const locationParts = [formData.city, formData.state_province, formData.continent].filter(Boolean);
      const location = locationParts.join(', ');

      const updatedTeam = await updateTeam(team.team_id, {
        ...formData,
        location
      });
      
      // Call the parent callback if provided to refresh the display
      if (onTeamUpdate) {
        onTeamUpdate(updatedTeam);
      }
      
      toast.success('Team updated successfully!');
      onClose();
    } catch (error) {
      console.error('Error updating team:', error);
      toast.error('Failed to update team. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLogoUpload = (logoUrl: string) => {
    console.log('Logo uploaded, updating form data:', logoUrl);
    setFormData(prev => ({ ...prev, team_logo: logoUrl }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-sports-green">Edit Team</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Team Logo */}
          <div className="space-y-2">
            <Label className="text-gray-300">Team Logo</Label>
            <TeamLogoUpload
              currentLogo={formData.team_logo}
              onLogoUpload={handleLogoUpload}
              teamName={formData.team_name}
            />
          </div>

          {/* Team Name */}
          <div className="space-y-2">
            <Label htmlFor="team_name" className="text-gray-300">
              Team Name *
            </Label>
            <Input
              id="team_name"
              type="text"
              value={formData.team_name}
              onChange={(e) => handleInputChange('team_name', e.target.value)}
              className="bg-gray-700 border-gray-600 text-white"
              placeholder="Enter team name"
              required
            />
          </div>

          {/* Sport Type */}
          <div className="space-y-2">
            <Label htmlFor="sport_type" className="text-gray-300">
              Sport *
            </Label>
            <Select value={formData.sport_type} onValueChange={(value) => handleInputChange('sport_type', value)}>
              <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                <SelectValue placeholder="Select sport" />
              </SelectTrigger>
              <SelectContent className="bg-gray-700 border-gray-600">
                {sportsOptions.map((sport) => (
                  <SelectItem key={sport} value={sport} className="text-white hover:bg-gray-600">
                    {sport}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Age Group */}
          <div className="space-y-2">
            <Label htmlFor="age_group" className="text-gray-300">
              Age Group
            </Label>
            <Select value={formData.age_group} onValueChange={(value) => handleInputChange('age_group', value)}>
              <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                <SelectValue placeholder="Select age group" />
              </SelectTrigger>
              <SelectContent className="bg-gray-700 border-gray-600">
                {ageGroupOptions.map((ageGroup) => (
                  <SelectItem key={ageGroup} value={ageGroup} className="text-white hover:bg-gray-600">
                    {ageGroup}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Division */}
          <div className="space-y-2">
            <Label htmlFor="division" className="text-gray-300">
              Division
            </Label>
            <Select value={formData.division} onValueChange={(value) => handleInputChange('division', value)}>
              <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                <SelectValue placeholder="Select division" />
              </SelectTrigger>
              <SelectContent className="bg-gray-700 border-gray-600">
                {divisionOptions.map((division) => (
                  <SelectItem key={division} value={division} className="text-white hover:bg-gray-600">
                    {division}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Team Description */}
          <div className="space-y-2">
            <Label htmlFor="team_description" className="text-gray-300">
              Team Description
            </Label>
            <Textarea
              id="team_description"
              value={formData.team_description}
              onChange={(e) => handleInputChange('team_description', e.target.value)}
              className="bg-gray-700 border-gray-600 text-white min-h-[100px]"
              placeholder="Tell us about your team..."
            />
          </div>

          {/* Location Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="continent" className="text-gray-300">
                Continent
              </Label>
              <Select value={formData.continent} onValueChange={(value) => handleInputChange('continent', value)}>
                <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                  <SelectValue placeholder="Select continent" />
                </SelectTrigger>
                <SelectContent className="bg-gray-700 border-gray-600">
                  {continentOptions.map((continent) => (
                    <SelectItem key={continent} value={continent} className="text-white hover:bg-gray-600">
                      {continent}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="state_province" className="text-gray-300">
                State/Province
              </Label>
              <Input
                id="state_province"
                type="text"
                value={formData.state_province}
                onChange={(e) => handleInputChange('state_province', e.target.value)}
                className="bg-gray-700 border-gray-600 text-white"
                placeholder="Enter state/province"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="city" className="text-gray-300">
                City
              </Label>
              <Input
                id="city"
                type="text"
                value={formData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                className="bg-gray-700 border-gray-600 text-white"
                placeholder="Enter city"
              />
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end gap-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="bg-sports-green text-black hover:bg-green-600 font-semibold"
              disabled={loading}
            >
              {loading ? 'Updating Team...' : 'Update Team'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditTeamModal;
